import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { codigoValidacaoService } from "../../services/codigoValidacaoService";
import { Cidade } from "../../types/cidade";
import { BairroSelectionModal } from "../../components/BairroSelectionModal";
import { municipioService, ufService } from "../../services/enderecoService";
import { UF, PaginatedList, Municipio } from "../../types/localidade";

interface TerritorioSelecionado {
  cidade: Cidade;
  tipo: "INTEIRA" | "PARCIAL";
  bairros: Set<number>;
  bairrosNomes: Map<number, string>;
}

export const NovoFranquia: React.FC = () => {
  const [email, setEmail] = useState("");
  const [ufs, setUfs] = useState<UF[]>([]);
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [cidadesFilterTerm, setCidadesFilterTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUfId, setSelectedUfId] = useState<string>("");
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const [selectedTerritorios, setSelectedTerritorios] = useState<
    Map<number, TerritorioSelecionado>
  >(new Map());
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    cidade: Cidade | null;
  }>({ isOpen: false, cidade: null });

  useEffect(() => {
    ufService.listar().then(setUfs);
  }, []);

  useEffect(() => {
    if (!selectedUfId) {
      setCidades([]);
      return;
    }
    setLoading(true);
    municipioService
      .listar({
        uf: selectedUfId,
        limit: 5000,
      })
      .then((response: Municipio[]) => {
        const cidadesData: Cidade[] = response.map((municipio) => ({
          ...municipio,
          estado: municipio.uf.nome,
          pais: "Brasil",
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
        }));
        setCidades(cidadesData);
      })
      .catch((err) => {
        console.error("Erro ao carregar cidades:", err);
        setError("Não foi possível carregar a lista de cidades.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedUfId]);

  const cidadesFiltradas = useMemo(() => {
    if (!cidadesFilterTerm.trim()) return cidades;
    return cidades.filter(
      (cidade) =>
        cidade.nome_original
          .toLowerCase()
          .includes(cidadesFilterTerm.toLowerCase()) ||
        cidade.nome.toLowerCase().includes(cidadesFilterTerm.toLowerCase())
    );
  }, [cidades, cidadesFilterTerm]);
    
  const ufOptions = ufs.map((uf) => ({ value: uf.id, label: uf.nome }));

  const handleSelectCidade = (cidade: Cidade) => {
    setSelectedTerritorios((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(cidade.id)) {
        newMap.delete(cidade.id);
      } else {
        newMap.set(cidade.id, {
          cidade,
          tipo: "INTEIRA",
          bairros: new Set(),
          bairrosNomes: new Map(),
        });
      }
      return newMap;
    });
  };

  const handleTerritorioTypeChange = (
    cidadeId: number,
    tipo: "INTEIRA" | "PARCIAL"
  ) => {
    setSelectedTerritorios((prev) => {
      const newMap = new Map(prev);
      const territorio = newMap.get(cidadeId);
      if (territorio) {
        territorio.tipo = tipo;
        if (tipo === "INTEIRA") {
          territorio.bairros.clear();
          territorio.bairrosNomes.clear();
        }
      }
      return newMap;
    });
  };

  const handleOpenBairroModal = (cidade: Cidade) => {
    setModalState({ isOpen: true, cidade });
  };

  const handleCloseBairroModal = () => {
    setModalState({ isOpen: false, cidade: null });
  };

  const handleSaveBairros = (
    cidadeId: number,
    bairros: Set<number>,
    bairrosNomes: Map<number, string>
  ) => {
    setSelectedTerritorios((prev) => {
      const newMap = new Map(prev);
      const territorio = newMap.get(cidadeId);
      if (territorio) {
        territorio.bairros = bairros;
        territorio.bairrosNomes = bairrosNomes;
      }
      return newMap;
    });
  };

  const handleGenerateCode = async () => {
    if (!email || selectedTerritorios.size === 0) {
      setError(
        "É necessário fornecer um email e selecionar pelo menos um território."
      );
      return;
    }
    setError(null);
    setIsGenerating(true);
    try {
      const municipios_completos: number[] = [];
      const regiao_municipio: { id_municipio: number; bairros: number[] }[] =
        [];

      selectedTerritorios.forEach((territorio) => {
        if (territorio.tipo === "INTEIRA") {
          municipios_completos.push(territorio.cidade.id);
        } else {
          regiao_municipio.push({
            id_municipio: territorio.cidade.id,
            bairros: Array.from(territorio.bairros),
          });
        }
      });

      const response = await codigoValidacaoService.criar({
        email_convidado: email,
        territorio_proposto: {
          municipios_completos,
          regiao_municipio,
        },
      });

      const link = `${window.location.origin}/register?codigo=${response.id}`;
      setGeneratedLink(link);
    } catch (err) {
      console.error("Erro ao gerar código:", err);
      setError("Ocorreu um erro ao gerar o código de vinculação.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {modalState.isOpen && modalState.cidade && (
        <BairroSelectionModal
          cidade={modalState.cidade}
          isOpen={modalState.isOpen}
          onClose={handleCloseBairroModal}
          initialSelectedBairros={
            selectedTerritorios.get(modalState.cidade.id)?.bairros || new Set()
          }
          onSave={(bairros, bairrosNomes) =>
            handleSaveBairros(modalState.cidade!.id, bairros, bairrosNomes)
          }
        />
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#29306a]">
          Gerar Convite para Nova Franquia
        </h1>
        <p className="mt-2 text-sm text-[#64748b]">
          Insira o email do convidado e selecione as cidades de atuação.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <h3 className="text-sm font-medium text-red-800">{error}</h3>
        </div>
      )}

      {generatedLink ? (
        <div className="bg-green-50 border border-green-200 rounded-md p-6 text-center">
          <h2 className="text-lg font-bold text-green-800">
            Link Gerado com Sucesso!
          </h2>
          <p className="mt-2 text-sm text-green-700">
            Copie e envie o link abaixo para o novo franqueado:
          </p>
          <div className="mt-4 bg-white p-4 rounded border border-gray-300">
            <input
              type="text"
              value={generatedLink}
              readOnly
              className="w-full bg-transparent border-none text-gray-700 focus:ring-0"
            />
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(generatedLink!);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="mt-4 inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
          >
            {copied ? "Copiado!" : "Copiar Link"}
          </button>
          <Link
            to="/franquias"
            className="mt-4 ml-4 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#32bef0]"
          >
            Voltar
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4 text-[#29306a]">
              Informações do Convidado
            </h2>
            <label
              htmlFor="email"
              className="block text-base font-medium text-[#29306a]"
            >
              Email do Convidado *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 focus:ring-[#32bef0] focus:border-[#32bef0] block w-full shadow-sm sm:text-base border-gray-300 rounded-md"
              placeholder="email.do.franqueado@exemplo.com"
            />
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4 text-[#29306a]">
              Selecionar Cidades
            </h2>
            <div className="flex flex-col md:flex-row gap-4 mb-4">
            <Select
                options={ufOptions}
                value={ufOptions.find(opt => opt.value === Number(selectedUfId))}
                onChange={(selectedOption) => {
                  setSelectedUfId(selectedOption ? String(selectedOption.value) : "");
                }}
                placeholder="Selecione um Estado"
                className="w-full md:w-1/4"
                isClearable
              />
              <input
                type="text"
                value={cidadesFilterTerm}
                onChange={(e) => setCidadesFilterTerm(e.target.value)}
                placeholder="Buscar cidade..."
                className="w-full md:w-3/4 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#32bef0]"
                disabled={!selectedUfId}
              />
            </div>

            {!selectedUfId ? (
              <div className="text-center py-12">
                <p className="text-lg text-gray-500">
                  Por favor, selecione um estado para ver as cidades.
                </p>
              </div>
            ) : loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32bef0]"></div>
              </div>
            ) : (
              <div className="overflow-hidden border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-12"></th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cidade
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        UF
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cidadesFiltradas.map((cidade) => (
                      <tr key={cidade.id} className="hover:bg-gray-50">
                        <td className="pl-4">
                          <input
                            type="checkbox"
                            checked={selectedTerritorios.has(cidade.id)}
                            onChange={() => handleSelectCidade(cidade)}
                            className="h-4 w-4 text-[#32bef0] focus:ring-[#2ba8d8] border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {cidade.nome_original}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {cidade.uf.sigla}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-2 text-xs text-gray-500">
                  {cidadesFiltradas.length} cidades encontradas
                </div>
              </div>
            )}
          </div>

          {selectedTerritorios.size > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-[#29306a]">
                Territórios Selecionados ({selectedTerritorios.size})
              </h3>
              <ul className="mt-4 space-y-4">
                {Array.from(selectedTerritorios.values()).map(
                  ({ cidade, tipo, bairros, bairrosNomes }) => (
                    <li key={cidade.id} className="p-4 border rounded-md">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">
                          {cidade.nome_original} - {cidade.uf.sigla}
                        </span>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`tipo-${cidade.id}`}
                              checked={tipo === "INTEIRA"}
                              onChange={() =>
                                handleTerritorioTypeChange(cidade.id, "INTEIRA")
                              }
                              className="h-4 w-4"
                            />
                            <span className="ml-2">Cidade Inteira</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`tipo-${cidade.id}`}
                              checked={tipo === "PARCIAL"}
                              onChange={() =>
                                handleTerritorioTypeChange(cidade.id, "PARCIAL")
                              }
                              className="h-4 w-4"
                            />
                            <span className="ml-2">Parcial (Bairros)</span>
                          </label>
                        </div>
                      </div>
                      {tipo === "PARCIAL" && (
                        <div className="mt-4">
                          <button
                            onClick={() => handleOpenBairroModal(cidade)}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            Gerenciar Bairros ({bairros.size} selecionados)
                          </button>
                          {bairros.size > 0 && (
                            <ul className="mt-2 text-xs text-gray-600 list-disc list-inside">
                              {Array.from(bairrosNomes.values()).map((nome) => (
                                <li key={nome}>{nome}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  )
                )}
              </ul>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <Link
              to="/franquias"
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#32bef0]"
            >
              Voltar
            </Link>
            <button
              onClick={handleGenerateCode}
              disabled={
                isGenerating || !email || selectedTerritorios.size === 0
              }
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-[#32bef0] px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-[#2ba8d8] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? "Gerando..." : "Gerar Código de Vinculação"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
